import { ActionButton, MessageBarType, Stack, Text } from "@fluentui/react";
import { BreakpointType } from "../../../../Container/ContainerFluid";
import { useBreakpoint } from "../../../../Container/ContainerStyles";
import { containerStyles, formContainerStyles, lgContainerStyles, lgSubTitleStyles, lgTitleContainerStyles, lgTitleStyles, mdContainerStyles, mdSubTitleStyles, mdTitleContainerStyles, mdTitleStyles, smContainerStyles, smSubTitleStyles, smTitleContainerStyles, smTitleStyles, subTitleStyles, titleContainerStyles, titleStyles, xlContainerStyles, xlSubTitleStyles, xlTitleContainerStyles, xlTitleStyles, xxlContainerStyles, xxlSubTitleStyles, xxlTitleContainerStyles, xxlTitleStyles } from "../../Common/Styles/CommonStyles";
import { useEffect, useState } from "react";
import { useRegisterContext } from "../../../RegisterContainer";
import { RegisterPrimaryButton, RegisterSecondaryButton } from "../../Common/RegisterButton";
import { RegisterTextField } from "../../Common/RegisterField";
import { fallbackMsAccountStyles, formButtonContainerStyles, innerTextStyles, lgFormButtonContainerStyles, mdFormButtonContainerStyles, sendEmailContainerStyles, smFormButtonContainerStyles, xlFormButtonContainerStyles, xxlFormButtonContainerStyles } from "./Styles/RegisterTrialGdapStyles";
import { LeadToLiveStep } from "../../../../../../Model/CommonModel";
import { RegisterService } from "../../../Service/RegisterService";
import { RegisterMessageBar } from "../../Common/RegisterErrorBar";
import { Trans } from "react-i18next";

export const RegisterTrialGDAP: React.FC = () => {
    const [showAdminEmail, setShowAdminEmail] = useState<boolean>(false);
    const [copyButtonText, setCopyButtonText] = useState<string>(RegisterService.translate("gdap:copyLinkText"));
    const [sendEmailButtonDisabled, setSendEmailButtonDisabled] = useState<boolean>(false);
    const [messageBarType, setMessageBarType] = useState<MessageBarType>(MessageBarType.info);
    const [showMessageBar, setShowMessageBar] = useState<boolean>(false);
    const [messageText, setMessageText] = useState<string>("");
    const [adminEmail, setAdminEmail] = useState<string>("");
    
    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);
    
    const registerContext = useRegisterContext();

    const copyLink = () => {   
        navigator.clipboard.writeText(getRelationshipUrl()).then(()=>{
            setCopyButtonText(RegisterService.translate("gdap:copiedLinkText"));
        })
    };

    const getRelationshipUrl = (): string => {
        switch(registerContext.leadToLive.currentStep){
            case LeadToLiveStep.GDAPAdmin:
                return registerContext.leadToLive.gdapAdminRelationship.signLink;
            case LeadToLiveStep.GDAPDyn365:
                return registerContext.leadToLive.gdapDynamics365Relationship.signLink;
            default:
                return "";
        }
    }

    const handleSendEmail = () => {
        setSendEmailButtonDisabled(true);
        setMessageBarType(MessageBarType.info);
        setMessageText(RegisterService.translate("gdap:sendInProgress"));
        setShowMessageBar(true);

        RegisterService.sendAdminEmail( adminEmail, getRelationshipUrl()).then((response) => {
            setSendEmailButtonDisabled(false);
            setMessageBarType(MessageBarType.success);
            setMessageText(RegisterService.translate("gdap:sendSuccess"));
            setShowMessageBar(true);
        })
        .catch((error) => {
            setMessageText(RegisterService.translate("gdap:sendError"));
            setMessageBarType(MessageBarType.error);
            setShowMessageBar(true);
            setSendEmailButtonDisabled(false);
        });
    };

    useEffect(() => {
        if (registerContext.leadToLive.currentStep !== LeadToLiveStep.GDAPDyn365 && registerContext.leadToLive.currentStep !== LeadToLiveStep.GDAPAdmin)
            return;

        let nextActionHidden: boolean = false;
        if(showAdminEmail)
            nextActionHidden = true;

        registerContext.setFooterButtonProps({
            ...registerContext.footerButtonProps,
            nextButtonHidden: nextActionHidden,
        });
    },[showAdminEmail]);
    
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                registerContext.onNext(registerContext.leadToLive);
            }
        };
    
        document.addEventListener("visibilitychange", handleVisibilityChange);
    
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    return (
        <Stack
            className={
                isSM ? 
                    smContainerStyles : 
                isMD ? 
                    mdContainerStyles :
                isLG ?
                    lgContainerStyles :
                isXL ?
                    xlContainerStyles :
                isXXL ?
                    xxlContainerStyles
                :
                    containerStyles
            }
        >
            <Stack
                className={
                    isSM ? 
                        smTitleContainerStyles : 
                    isMD ? 
                        mdTitleContainerStyles :
                    isLG ?
                        lgTitleContainerStyles :
                    isXL ?
                        xlTitleContainerStyles :
                    isXXL ?
                        xxlTitleContainerStyles
                    :
                        titleContainerStyles
                }
            >
                <Text 
                    className={
                        isSM ? 
                            smTitleStyles : 
                        isMD ? 
                            mdTitleStyles : 
                        isLG ?
                            lgTitleStyles :
                        isXL ?
                            xlTitleStyles :
                        isXXL ?
                            xxlTitleStyles
                        :
                            titleStyles
                    }
                >
                    {RegisterService.translate("gdap:title")}
                </Text>
                <Text 
                    className={
                        isSM ? 
                            smSubTitleStyles : 
                        isMD ? 
                            mdSubTitleStyles :
                        isLG ?
                            lgSubTitleStyles :
                        isXL ?
                            xlSubTitleStyles :
                        isXXL ?
                            xxlSubTitleStyles
                        :
                            subTitleStyles
                    }
                >
                    <Trans 
                        i18nKey="common:register:gdap:description"
                        components={[<strong />]}
                    />
                </Text>
            </Stack>
            <Stack
                className={formContainerStyles}
                horizontal
                horizontalAlign="center"
            >
                <Stack
                    className={
                        isSM ? 
                            smFormButtonContainerStyles : 
                        isMD ? 
                            mdFormButtonContainerStyles : 
                        isLG ?
                            lgFormButtonContainerStyles :
                        isXL ?
                            xlFormButtonContainerStyles :
                        isXXL ?
                            xxlFormButtonContainerStyles
                        :
                            formButtonContainerStyles
                    }
                >
                    <RegisterMessageBar 
                        message={messageText}
                        type={messageBarType}
                        visible={showMessageBar}
                    />
                    {
                        !showAdminEmail &&
                        <Stack
                            verticalFill
                            verticalAlign="center"
                        >
                            <RegisterPrimaryButton 
                                iconName="Permissions"
                                infoText=""
                                text={RegisterService.translate("gdap:permissionButton")}
                                onClick={() => {window.open(getRelationshipUrl(), '_blank')}}
                            />
                            <ActionButton
                                iconProps={{ iconName: "Copy" }}
                                styles={fallbackMsAccountStyles}
                                onClick={() => {
                                    copyLink();
                                }}
                            >
                                {copyButtonText}
                            </ActionButton>
                            <Text
                                className={innerTextStyles}
                            >
                                {RegisterService.translate("gdap:divider")}
                            </Text>
                            <RegisterSecondaryButton 
                                iconName="MailForward"
                                infoText=""
                                text={RegisterService.translate("gdap:emailButton")}
                                onClick={() => {
                                    setShowAdminEmail(true);
                                }}
                            />
                        </Stack>
                    }
                    {
                        showAdminEmail &&
                            <>
                                <Stack
                                    verticalFill
                                    verticalAlign="center"
                                    className={sendEmailContainerStyles}
                                >
                                    <RegisterTextField 
                                        value={adminEmail}
                                        placeholder={RegisterService.translate("gdap:emailPlaceholder")}
                                        onChange={(value) => {setAdminEmail(value)}}
                                    />
                                    <RegisterPrimaryButton 
                                        iconName="Mail"
                                        infoText=""
                                        text={RegisterService.translate("gdap:sendEmailButton")}
                                        disabled={sendEmailButtonDisabled}
                                        onClick={() => {
                                            handleSendEmail()
                                        }}
                                    />
                                    <ActionButton
                                        iconProps={{ iconName: "Permissions" }}
                                        styles={fallbackMsAccountStyles}
                                        onClick={() => {
                                            setShowAdminEmail(false);
                                            setShowMessageBar(false);
                                        }}
                                    >
                                        {RegisterService.translate("gdap:backButton")}
                                    </ActionButton>
                                </Stack>
                            </>
                    }
                </Stack>
            </Stack>
        </Stack>
    );
};